/* eslint-disable no-undef */

import { database, storage } from '../firebaseConfig';
import { ref as dbRef, get } from "firebase/database";
import { ref as storageRef, getDownloadURL } from "firebase/storage";

const ITEMS_PER_PAGE = 12;

// First, get all albums and cache them
let cachedAlbums = null;

export const fetchRootAlbums = async (lastTimestamp = null) => {
  try {
    // If we don't have cached albums, fetch them all
    if (!cachedAlbums) {
      const albumsRef = dbRef(database, 'gallery/legacy/albums');
      const snapshot = await get(albumsRef);
      const allAlbums = snapshot.val() || {};
      
      // Debug: Log the raw data structure
      console.log('Raw albums data:', allAlbums);
      
      // Convert to array with all albums (no filtering yet)
      cachedAlbums = Object.entries(allAlbums)
        .map(([id, album]) => ({
          id,
          ...album,
          parentAlbum: album.parent_album || null
        }));

      console.log('All albums before filtering:', cachedAlbums.length);
      console.log('Sample album structure:', cachedAlbums[0]);
      
      // Now filter for root albums
      cachedAlbums = cachedAlbums
        .filter(album => {
          const isRoot = !album.parentAlbum || 
                        album.parentAlbum === 'root' || 
                        album.parentAlbum === 'Null' || 
                        album.parentAlbum === '';
          return isRoot;
        })
        .sort((a, b) => b.creation_date - a.creation_date);

      console.log('Total root albums found:', cachedAlbums.length);
      console.log('Sample root album:', cachedAlbums[0]);
    }

    // Determine which slice of albums to return
    let startIndex = 0;
    if (lastTimestamp) {
      startIndex = cachedAlbums.findIndex(album => album.creation_date === lastTimestamp) + 1;
      console.log('Starting from index:', startIndex);
    }

    const batch = cachedAlbums.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    console.log('Batch size:', batch.length);

    // Add cover URLs
    const albumsWithCover = await Promise.all(
      batch.map(async (album) => {
        let coverUrl = '/default-cover.jpg';
        
        if (album.cover_image) {
          try {
            if (album.cover_image.startsWith('http')) {
              coverUrl = album.cover_image;
            } else {
              const photoRef = dbRef(database, `gallery/legacy/photos/${album.cover_image}`);
              const photoSnapshot = await get(photoRef);
              if (photoSnapshot.exists()) {
                const photoData = photoSnapshot.val();
                coverUrl = await getPhotoUrl(photoData.url);
              }
            }
          } catch (error) {
            console.error('Error processing cover image:', error);
          }
        }

        return {
          ...album,
          coverUrl
        };
      })
    );

    return {
      albums: albumsWithCover,
      hasMore: startIndex + ITEMS_PER_PAGE < cachedAlbums.length,
      total: cachedAlbums.length
    };
  } catch (error) {
    console.error('Error fetching root albums:', error);
    return { albums: [], hasMore: false, total: 0 };
  }
};

export const fetchSubAlbums = async (parentAlbumId) => {
  try {
    const albumsRef = dbRef(database, 'gallery/legacy/albums');
    const snapshot = await get(albumsRef);
    const albums = snapshot.val() || {};

    const subAlbumsArray = Object.entries(albums)
      .filter(([_, album]) => album.parent_album === parentAlbumId)
      .map(([id, album]) => ({
        id,
        ...album
      }));

    const sortedSubAlbums = subAlbumsArray.sort((a, b) => b.creation_date - a.creation_date);

    const albumsWithCover = await Promise.all(
      sortedSubAlbums.map(async (album) => {
        const coverUrl = await getCoverImageUrl(album.cover_image);
        return {
          ...album,
          coverUrl
        };
      })
    );

    return albumsWithCover;
  } catch (error) {
    console.error('Error fetching sub-albums:', error);
    return [];
  }
};

export const fetchAlbumPhotos = async (albumId, lastPhotoId = null) => {
  try {
    const photosRef = dbRef(database, 'gallery/legacy/photos');
    const snapshot = await get(photosRef);
    const photos = snapshot.val() || {};

    const albumPhotosArray = Object.entries(photos)
      .filter(([_, photo]) => photo.parent_album === albumId)
      .map(([id, photo]) => ({
        id,
        ...photo
      }));

    const sortedPhotos = albumPhotosArray.sort((a, b) => b.creation_date - a.creation_date);

    if (lastPhotoId) {
      const currentIndex = sortedPhotos.findIndex(photo => photo.id === lastPhotoId);
      const nextBatch = sortedPhotos.slice(currentIndex + 1, currentIndex + 1 + ITEMS_PER_PAGE);
      const photosWithUrls = await Promise.all(
        nextBatch.map(async (photo) => {
          const url = await getPhotoUrl(photo.url);
          return {
            ...photo,
            url
          };
        })
      );
      return photosWithUrls;
    }

    const firstBatch = sortedPhotos.slice(0, ITEMS_PER_PAGE);
    const photosWithUrls = await Promise.all(
      firstBatch.map(async (photo) => {
        const url = await getPhotoUrl(photo.url);
        return {
          ...photo,
          url
        };
      })
    );

    return photosWithUrls;
  } catch (error) {
    console.error('Error fetching album photos:', error);
    return [];
  }
};

export const getCoverImageUrl = async (coverImageId) => {
  if (!coverImageId || coverImageId === 'Null') {
    return '/default-cover.jpg';
  }

  try {
    const photoRef = dbRef(database, `gallery/legacy/photos/${coverImageId}`);
    const snapshot = await get(photoRef);
    
    if (snapshot.exists()) {
      const photoData = snapshot.val();
      return await getPhotoUrl(photoData.url);
    }
    
    return '/default-cover.jpg';
  } catch (error) {
    console.error('Error fetching cover image:', error);
    return '/default-cover.jpg';
  }
};

export const getPhotoUrl = async (path) => {
  if (!path) return '/default-photo.jpg';
  
  if (path.startsWith('http')) return path;
  
  try {
    const formattedPath = path.replace('gs://baue-gallery.appspot.com/', '');
    const photoRef = storageRef(storage, formattedPath);
    return await getDownloadURL(photoRef);
  } catch (error) {
    console.error('Error getting photo URL:', error);
    return '/default-photo.jpg';
  }
};

export const getAlbums = async (startIndex = 0, batchSize = 12) => {
  try {
    const albumsRef = dbRef(database, 'gallery/legacy/albums');
    const snapshot = await get(albumsRef);
    
    if (!snapshot.exists()) {
      console.warn('No albums found in database');
      return { albums: [], total: 0 };
    }

    const albumsData = snapshot.val();
    
    // Convert to array and add IDs
    const allAlbums = Object.entries(albumsData).map(([id, album]) => ({
      id,
      ...album
    }));

    // Debug: Let's look at different parent_album values
    const parentTypes = new Set();
    allAlbums.forEach(album => {
      parentTypes.add(album.parent_album);
    });
    console.log('All parent_album types:', Array.from(parentTypes));

    // Debug: Sample of non-root albums
    const nonRootAlbums = allAlbums.filter(album => album.parent_album !== 'root');
    console.log('Sample of non-root albums:', nonRootAlbums.slice(0, 5));

    // Original root album logic
    const rootAlbums = allAlbums.filter(album => album.parent_album === 'root');
    
    console.log(`Found ${rootAlbums.length} root albums out of ${allAlbums.length} total albums`);

    // Sort by creation date (newest first)
    rootAlbums.sort((a, b) => b.creation_date - a.creation_date);

    // Get paginated subset
    const paginatedAlbums = rootAlbums.slice(startIndex, startIndex + batchSize);

    return {
      albums: paginatedAlbums,
      total: rootAlbums.length,
      hasMore: startIndex + batchSize < rootAlbums.length
    };
  } catch (error) {
    console.error('Error fetching albums:', error);
    throw error;
  }
};